import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Stack,
  Switch,
  FormControlLabel,
  Skeleton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Reply as ReplyIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Flag as FlagIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { createApiUrl } from '../../config/api';

const TicketDetailPage = ({ ticketId, onBack }) => {
  
  const [ticketData, setTicketData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyLoading, setReplyLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Reply form
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [replyError, setReplyError] = useState('');

  // Admin controls
  const [availableUsers, setAvailableUsers] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  // Fetch ticket details
  const fetchTicketDetails = useCallback(async () => {
    if (!ticketId) return;
    
    try {
      setLoading(true);
      const response = await fetch(createApiUrl(`api/tickets/${ticketId}`), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await response.json();
      
      if (response.ok) {
        setTicketData(data.ticket);
        setNotes(data.notes || []);
      } else {
        setError(data.error || 'Failed to fetch ticket details');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  // Fetch metadata for admin controls
  const fetchMetadata = useCallback(async () => {
    try {
      const authHeader = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      const [usersRes, metadataRes] = await Promise.all([
        fetch(createApiUrl('api/tickets/users/available'), { headers: authHeader }),
        fetch(createApiUrl('api/tickets/metadata'), { headers: authHeader })
      ]);
      
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setAvailableUsers(usersData.users || []);
      }
      
      if (metadataRes.ok) {
        const metadataData = await metadataRes.json();
        setPriorities(metadataData.priorities || []);
      }
    } catch (err) {
      console.error('Failed to fetch metadata:', err);
    }
  }, []);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails();
      fetchMetadata();
    }
  }, [ticketId, fetchTicketDetails, fetchMetadata]);

  // Handle reply submission
  const handleReplySubmit = async () => {
    if (!replyText.trim()) {
      setReplyError('Reply text is required');
      return;
    }

    try {
      setReplyLoading(true);
      const response = await fetch(createApiUrl(`api/tickets/${ticketId}/notes`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          content: replyText,
          message_type: isInternalNote ? 'internal_note' : 'reply',
          is_internal: isInternalNote,
          created_by: 'admin' // Would come from auth context
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setReplyText('');
        setReplyError('');
        setSuccess(isInternalNote ? 'Internal note added' : 'Reply sent to customer');
        fetchTicketDetails(); // Refresh to show new reply
      } else {
        setReplyError(data.error || 'Failed to submit reply');
      }
    } catch (error) {
      setReplyError('Network error occurred');
    } finally {
      setReplyLoading(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    try {
      setStatusLoading(true);
      const response = await fetch(createApiUrl(`api/tickets/${ticketId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          status: newStatus,
          modified_by: 'admin' // Would come from auth context
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Ticket status updated');
        fetchTicketDetails(); // Refresh to show updated status
      } else {
        setError(data.error || 'Failed to update status');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setStatusLoading(false);
    }
  };

  // Handle assignment change
  const handleAssignmentChange = async (userId) => {
    try {
      setAssignmentLoading(true);
      const response = await fetch(createApiUrl(`api/tickets/${ticketId}/assign`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          assigned_to_user_id: userId || null,
          assigned_by_user_id: 1 // Would come from auth context
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Assignment updated');
        fetchTicketDetails();
      } else {
        setError(data.error || 'Failed to update assignment');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setAssignmentLoading(false);
    }
  };

  // Handle priority change
  const handlePriorityChange = async (priorityId) => {
    try {
      setPriorityLoading(true);
      const response = await fetch(createApiUrl(`api/tickets/${ticketId}/priority`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          priority_id: priorityId,
          modified_by: 'admin'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Priority updated');
        fetchTicketDetails();
      } else {
        setError(data.error || 'Failed to update priority');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setPriorityLoading(false);
    }
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'warning'; // Open
      case 2: return 'success'; // Resolved
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1: return 'Open';
      case 2: return 'Resolved';
      default: return 'Unknown';
    }
  };

  const getPriorityColor = (priorityName) => {
    switch (priorityName?.toLowerCase()) {
      case 'low': return 'success';
      case 'normal': return 'info';
      case 'high': return 'warning';
      case 'critical': return 'error';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getMessageIcon = (message) => {
    if (message.is_internal) {
      return <LockIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    } else if (message.message_type === 'reply') {
      return <ReplyIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    } else {
      return <AssignmentIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
    }
  };

  const getMessageTypeText = (message) => {
    if (message.is_internal) {
      return 'Internal Note';
    } else if (message.message_type === 'reply') {
      return 'Customer Reply';
    } else {
      return 'System Message';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Skeleton variant="rectangular" width="100%" height={300} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error && !ticketData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!ticketData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Ticket not found
        </Alert>
        <Button onClick={onBack} startIcon={<ArrowBackIcon />}>
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, md: 3 }, width: '100%' }}>
        {/* Back Button Row */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
          <Button 
            onClick={onBack} 
            startIcon={<ArrowBackIcon />}
            size="small"
          >
            Back to Tickets
          </Button>
        </Box>
        
        {/* Ticket Title and Info Row */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 1 },
            width: '100%'
          }}
        >
          <Box sx={{ flexGrow: 1, textAlign: 'left' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.125rem' },
                mb: 1,
                textAlign: 'left'
              }}
            >
              Ticket #{ticketData.id}: {ticketData.name}
            </Typography>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'flex-start'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip 
                  label={getStatusText(ticketData.status)} 
                  color={getStatusColor(ticketData.status)} 
                  size="small"
                />
                {ticketData.priority_name && (
                  <Chip 
                    label={ticketData.priority_name} 
                    color={getPriorityColor(ticketData.priority_name)} 
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                Created {formatDateTime(ticketData.created_date)}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={fetchTicketDetails} 
            disabled={loading}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box 
        sx={{ 
          display: 'flex', 
          gap: { xs: 2, md: 3 },
          flexDirection: { xs: 'column', lg: 'row' }
        }}
      >
        {/* Main Content - Conversation */}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {/* Original Ticket */}
          <Card sx={{ mb: { xs: 2, md: 3 } }}>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                <Avatar sx={{ 
                  mr: { xs: 1, md: 2 }, 
                  bgcolor: 'primary.main',
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 }
                }}>
                  <PersonIcon fontSize="medium" />
                </Avatar>
                <Box>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold"
                    sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    {ticketData.customer_name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                  >
                    {formatDateTime(ticketData.created_date)} • Initial Request
                  </Typography>
                </Box>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  lineHeight: 1.6, 
                  textAlign: 'left',
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}
              >
                {ticketData.notes}
              </Typography>
            </CardContent>
          </Card>

          {/* Conversation Thread */}
          {notes.map((note, index) => (
            <Card 
              key={note.id} 
              sx={{ 
                mb: { xs: 1.5, md: 2 }, 
                ml: { xs: note.is_internal ? 1 : 0, md: note.is_internal ? 2 : 0 },
                border: note.is_internal ? '1px solid' : 'none',
                borderColor: note.is_internal ? 'warning.light' : 'transparent',
                bgcolor: note.is_internal ? 'warning.50' : 'background.paper'
              }}
            >
              <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
                  <Avatar sx={{ 
                    mr: { xs: 1, md: 2 }, 
                    bgcolor: note.is_internal ? 'warning.main' : 'secondary.main',
                    width: { xs: 28, md: 36 },
                    height: { xs: 28, md: 36 }
                  }}>
                    {getMessageIcon(note)}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography 
                        variant="subtitle2" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                      >
                        {note.created_by_name || note.created_by}
                      </Typography>
                      {note.created_by_role && (
                        <Chip 
                          label={note.created_by_role} 
                          size="small" 
                          sx={{ ml: 0.5, height: { xs: 16, md: 20 }, fontSize: { xs: '0.6rem', md: '0.75rem' } }}
                        />
                      )}
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' }
                    }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' } }}
                      >
                        {formatDateTime(note.created_at)}
                      </Typography>
                      <Chip 
                        label={getMessageTypeText(note)}
                        size="small"
                        color={note.is_internal ? 'warning' : 'primary'}
                        variant="outlined"
                        sx={{ 
                          height: { xs: 16, md: 20 },
                          fontSize: { xs: '0.6rem', md: '0.75rem' }
                        }}
                      />
                    </Box>
                  </Box>
                  {note.is_internal && (
                    <Tooltip title="Internal Note - Not visible to customer">
                      <LockIcon fontSize="small" color="warning" />
                    </Tooltip>
                  )}
                </Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap', 
                    lineHeight: 1.6, 
                    textAlign: 'left',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {note.content}
                </Typography>
              </CardContent>
            </Card>
          ))}

          {/* Reply Form */}
          <Card>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
              >
                Add Response
              </Typography>
              
              {replyError && (
                <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  {replyError}
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={isInternalNote}
                    onChange={(e) => setIsInternalNote(e.target.checked)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon fontSize="small" />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Internal Note (Staff Only)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label={isInternalNote ? "Internal Note" : "Reply to Customer"}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={isInternalNote ? 
                  "Add an internal note that only staff can see..." :
                  "Add your reply to the customer..."
                }
                sx={{ mb: 2 }}
              />
              
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleReplySubmit}
                disabled={replyLoading || !replyText.trim()}
                color={isInternalNote ? 'warning' : 'primary'}
              >
                {replyLoading ? 'Adding...' : (isInternalNote ? 'Add Internal Note' : 'Send Reply')}
              </Button>
            </CardContent>
          </Card>
        </Box>

        {/* Right Sidebar - Admin Controls */}
        <Box sx={{ width: 350, flexShrink: 0 }}>
          {/* Customer Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {ticketData.customer_name}
                  </Typography>
                  {ticketData.company_name && (
                    <Typography variant="caption" color="text.secondary">
                      {ticketData.company_name}
                    </Typography>
                  )}
                </Box>
              </Box>

              {ticketData.email_address && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{ticketData.email_address}</Typography>
                </Box>
              )}

              {ticketData.phone_number && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{ticketData.phone_number}</Typography>
                </Box>
              )}

              {(ticketData.street_address || ticketData.city) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary', mt: 0.2 }} />
                  <Box>
                    {ticketData.street_address && (
                      <Typography variant="body2">{ticketData.street_address}</Typography>
                    )}
                    <Typography variant="body2">
                      {[ticketData.city, ticketData.province, ticketData.postal_code]
                        .filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Ticket Management */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ticket Management
              </Typography>
              
              <Stack spacing={2}>
                {/* Status */}
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={ticketData.status}
                    label="Status"
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusLoading}
                  >
                    <MenuItem value={1}>Open</MenuItem>
                    <MenuItem value={2}>Resolved</MenuItem>
                  </Select>
                </FormControl>

                {/* Assignment */}
                <FormControl fullWidth>
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={ticketData.assigned_to_user_id || ''}
                    label="Assigned To"
                    onChange={(e) => handleAssignmentChange(e.target.value)}
                    disabled={assignmentLoading}
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.user_type})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Priority */}
                {priorities.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={ticketData.priority_id || ''}
                      label="Priority"
                      onChange={(e) => handlePriorityChange(e.target.value)}
                      disabled={priorityLoading}
                    >
                      {priorities.map((priority) => (
                        <MenuItem key={priority.id} value={priority.id}>
                          {priority.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Category */}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {ticketData.category_name}
                  </Typography>
                </Box>

                {/* Created */}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(ticketData.created_date)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              
              <Stack spacing={1}>
                <Button 
                  variant="outlined" 
                  startIcon={<EditIcon />}
                  fullWidth
                  onClick={() => {/* Navigate to edit form */}}
                >
                  Edit Ticket
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<FlagIcon />}
                  fullWidth
                  color="warning"
                >
                  Mark as Priority
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<VisibilityIcon />}
                  fullWidth
                  color="info"
                >
                  View Customer Profile
                </Button>
              </Stack>
            </CardContent>
                      </Card>
          </Box>
        </Box>
      </Box>
    );
  };

  export default TicketDetailPage; 